import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { LanguageProvider } from './context/LanguageContext';
import Layout from './components/layout/Layout';
import { useTranslation } from 'react-i18next';
import { useLanguage } from './context/LanguageContext';
import LoadingSpinner from './components/ui/LoadingSpinner';
import { pageview } from './utils/analytics';
import './App.css';
import './i18n';

// Lazy load pages for better performance
const HomePage = lazy(() => import('./pages/HomePage'));
const HotelsPage = lazy(() => import('./pages/HotelsPage'));
const HotelDetailPage = lazy(() => import('./pages/HotelDetailPage'));
const TransportationPage = lazy(() => import('./pages/TransportationPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const BlogListPage = lazy(() => import('./pages/BlogListPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const GuidesPage = lazy(() => import('./pages/GuidesPage'));
const PackagesPage = lazy(() => import('./pages/PackagesPage'));

// Component for temporary pages
const TempPage: React.FC<{ title: string }> = ({ title }) => {
  const { direction } = useLanguage();
  const isRTL = direction === 'rtl';
  
  return (
    <div className="p-10 text-center">
      <h1 className="text-3xl font-bold text-emerald-600 mb-4">{title}</h1>
      <p className={`text-lg text-gray-600 ${isRTL ? 'font-arabic' : ''}`}>
        {isRTL 
          ? 'هذه الصفحة قيد الإنشاء وستكون متاحة قريبًا' 
          : 'This page is under construction and will be available soon'}
      </p>
    </div>
  );
};

function AppContent() {
  const { t } = useTranslation();
  const { language, direction } = useLanguage();
  const isRTL = direction === 'rtl';
  const location = useLocation();

  // Track page views and scroll to top on route change
  useEffect(() => {
    // Scroll to top for every navigation
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    // Analytics pageview
    pageview(location.pathname + location.search, document.title);
  }, [location]);

  // Custom titles for pages
  const hotelTitle = isRTL ? 'فنادقنا المميزة في مكة والمدينة' : 'Our Distinguished Hotels in Makkah and Madinah';
  const busesTitle = isRTL ? 'أسطول باصاتنا الحديثة والمريحة' : 'Our Modern and Comfortable Bus Fleet';
  const contactTitle = isRTL ? 'تواصل معنا' : 'Contact Us';
  const notFoundTitle = isRTL ? 'الصفحة غير موجودة' : 'Page Not Found';

  return (
    <Layout>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/hotels" element={<HotelsPage />} />
          <Route path="/hotels/:hotelId" element={<HotelDetailPage />} />
          <Route path="/buses" element={<TransportationPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/blog" element={<BlogListPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/services/guides" element={<GuidesPage />} />
          <Route path="/packages" element={<PackagesPage />} />
          <Route path="*" element={<TempPage title={notFoundTitle} />} />
        </Routes>
      </Suspense>
    </Layout>
  );
}

function App() {
  return (
    <LanguageProvider>
      <Router basename={process.env.PUBLIC_URL}>
        <AppContent />
      </Router>
    </LanguageProvider>
  );
}

export default App;
